import React, { useEffect, useState } from 'react';
import { EventCard } from '../components/EventCard';
import { eventsService } from '../../../core/services/eventsService';
import { CommunityEvent } from '../../../core/types/events';
import { Filter, Calendar, MapPin, PlusCircle } from 'lucide-react';
import { BRAZILIAN_STATES } from '../data/brazilianStates';

export const EventsPage: React.FC = () => {
  const [events, setEvents] = useState<CommunityEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedState, setSelectedState] = useState('');
  const [selectedType, setSelectedType] = useState('');

  useEffect(() => {
    loadEvents();
  }, [selectedState, selectedType]);

  const loadEvents = async () => {
    setLoading(true);
    try {
      const data = await eventsService.getPublicEvents({
        state: selectedState || undefined,
        type: selectedType || undefined
      });
      setEvents(data);
    } catch (error) {
      console.error('Erro ao carregar eventos:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-midnight flex flex-col">
      
      {/* Header da Página */}
      <div className="bg-white/5 border-b border-white/10 py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-glow-mesh-dark opacity-10"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-black mb-4 font-display uppercase tracking-tight text-white">
              Calendário da Pesca
            </h1>
            <p className="text-xl text-white/40 mb-8 font-medium">
              Torneios, festivais e encontros de pesca esportiva em todo o Brasil.
            </p>
            
            <button className="bg-brand-orange text-white font-black px-8 py-4 rounded-xl flex items-center gap-2 transition-all shadow-glow-orange hover:scale-105 uppercase tracking-widest text-sm">
              <PlusCircle size={20} />
              Divulgar meu Evento
            </button>
          </div>
        </div>
      </div>
      
      {/* Filtros e Conteúdo */}
      <div className="container mx-auto px-6 py-10 flex-grow">
        <div className="grid lg:grid-cols-4 gap-8">
          
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-brand-charcoal/30 rounded-2xl p-6 border border-white/10 sticky top-6">
              <h3 className="font-black text-xs uppercase tracking-[0.3em] text-white/30 mb-8 flex items-center gap-2">
                <Filter size={14} className="text-brand-gold" />
                Filtrar Eventos
              </h3>
              
              <div className="mb-8">
                <label htmlFor="state-filter" className="block text-[10px] font-black text-white/30 uppercase tracking-widest mb-3">Estado</label>
                <select 
                  id="state-filter"
                  className="w-full border border-white/10 bg-white/5 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-brand-gold/30 outline-none transition-all text-sm"
                  value={selectedState}
                  onChange={(e) => setSelectedState(e.target.value)}
                  title="Filtrar por Estado"
                >
                  <option value="" className="bg-brand-midnight text-white">Todo o Brasil</option>
                  {BRAZILIAN_STATES.map(state => (
                    <option key={state.code} value={state.code} className="bg-brand-midnight text-white">{state.name}</option>
                  ))}
                </select>
              </div>
              
              <div className="mb-8">
                <label htmlFor="type-filter" className="block text-[10px] font-black text-white/30 uppercase tracking-widest mb-3">Tipo de Evento</label>
                <select 
                  id="type-filter"
                  className="w-full border border-white/10 bg-white/5 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-brand-gold/30 outline-none transition-all text-sm"
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  title="Filtrar por Tipo de Evento"
                >
                  <option value="" className="bg-brand-midnight text-white">Todos os Tipos</option>
                  <option value="tournament" className="bg-brand-midnight text-white">🏆 Torneios</option>
                  <option value="festival" className="bg-brand-midnight text-white">🎉 Festivais</option>
                  <option value="workshop" className="bg-brand-midnight text-white">👨‍🏫 Cursos/Workshops</option>
                  <option value="gathering" className="bg-brand-midnight text-white">🤝 Encontros</option>
                </select>
              </div>

              <div className="mt-8 bg-white/5 p-6 rounded-2xl border border-white/5 text-xs text-white/40 font-medium">
                <p className="mb-2 font-black text-brand-gold uppercase tracking-widest text-[10px]">📢 Organizadores</p>
                <p>Cadastre seu torneio gratuitamente e alcance milhares de pescadores.</p>
              </div>
            </div>
          </div>
          
          {/* Lista de Eventos */}
          <div className="lg:col-span-3">
            {loading ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map(i => (
                  <div key={i} className="bg-white/5 h-80 rounded-3xl animate-pulse border border-white/10"></div>
                ))}
              </div>
            ) : events.length === 0 ? (
              <div className="text-center py-20 bg-white/5 rounded-3xl border border-dashed border-white/10">
                <Calendar size={64} className="mx-auto text-white/10 mb-4" />
                <h3 className="text-xl font-black text-white mb-2 font-display">Nenhum evento encontrado</h3>
                <p className="text-white/40 font-medium">Tente mudar os filtros de busca.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {events.map(event => (
                  <EventCard key={event.id} event={event} />
                ))}
              </div>
            )}
          </div>
          
        </div>
      </div>
    </div>
  );
};
